import { useState, useEffect, useCallback } from 'react';
import type { Product } from '@/types/product';
import { getPriceNum } from '@/types/product';
import { voucherApi } from '@/api-client';

const CART_KEY = 'jl-shop-cart';

interface CartItem {
  product: Product;
  quantity: number;
}

interface Voucher {
  code: string;
  amount: number;
  type?: string; // 'fixed' | 'percent'
}

interface SerializedCartItem {
  productId: number;
  productSlug: string;
  productName: string;
  productPrice: string;
  productImage: string;
  productCategory: string;
  quantity: number;
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed: SerializedCartItem[] = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => ({
      product: {
        id: item.productId,
        slug: item.productSlug,
        name: item.productName,
        description: '',
        longDescription: null,
        price: item.productPrice,
        originalPrice: null,
        image: item.productImage,
        category: item.productCategory,
        badge: null,
        badgeColor: null,
        features: null,
        inStock: true,
        rating: null,
        reviews: null,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      quantity: item.quantity,
    }));
  } catch {
    return [];
  }
}

function saveCart(cart: CartItem[]) {
  const serialized: SerializedCartItem[] = cart.map((item) => ({
    productId: item.product.id,
    productSlug: item.product.slug,
    productName: item.product.name,
    productPrice: item.product.price,
    productImage: item.product.image,
    productCategory: item.product.category,
    quantity: item.quantity,
  }));
  localStorage.setItem(CART_KEY, JSON.stringify(serialized));
}

export function useCart() {
  const [cart, setCart] = useState<CartItem[]>(loadCart);
  const [isOpen, setIsOpen] = useState(false);
  const [voucher, setVoucher] = useState<Voucher | null>(null);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  // Gutschein anwenden – KORRIGIERT: voucherApi.check gibt KEIN .valid zurück!
  const applyVoucher = useCallback(async (code: string) => {
    try {
      const result = await voucherApi.check(code);
      // Bei Erfolg gibt die API { code, amount, type } zurück – NICHT { valid: true }
      if (result && result.code && typeof result.amount === 'number') {
        setVoucher({ code: result.code, amount: result.amount, type: result.type });
        return { success: true, amount: result.amount, type: result.type };
      }
    } catch (e: any) {
      return { success: false, error: e.message || 'Ungültiger Code' };
    }
    return { success: false, error: 'Ungültiger Code' };
  }, []);

  const removeVoucher = useCallback(() => {
    setVoucher(null);
  }, []);

  const addItem = useCallback((product: Product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((productId: number) => {
    setCart((prev) => {
      const next = prev.filter((item) => item.product.id !== productId);
      if (next.length === 0) setVoucher(null);
      return next;
    });
  }, []);

  const updateQuantity = useCallback(
    (productId: number, quantity: number) => {
      if (quantity <= 0) {
        setCart((prev) => prev.filter((item) => item.product.id !== productId));
        return;
      }
      setCart((prev) =>
        prev.map((item) =>
          item.product.id === productId ? { ...item, quantity } : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setCart([]);
    setVoucher(null);
  }, []);

  // Preisberechnung mit Gutschein (€ oder %)
  const rawTotal = cart.reduce(
    (sum, item) => sum + getPriceNum(item.product.price) * item.quantity,
    0
  );

  let discount = 0;
  if (voucher) {
    if (voucher.type === 'percent') {
      discount = rawTotal * (voucher.amount / 100);
    } else {
      discount = Math.min(voucher.amount, rawTotal);
    }
  }
  const totalPrice = Math.max(0, rawTotal - discount);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  // VERSAND: Nur bei physical Produkten nötig
  const needsShipping = cart.some((item) => item.product.category === 'physical');

  return {
    cart,
    isOpen,
    setIsOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
    rawTotal,
    discount,
    voucher,
    applyVoucher,
    removeVoucher,
    needsShipping,
  };
}